import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/auth";
import { useI18n } from "@/i18n";
import { useTheme, ThemeToggle } from "@/context/theme";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, apiErrorMessage } from "@/lib/api";
import { Btn, Input, Field } from "@/components/ui-rp";
import { StatusFooter } from "@/components/StatusFooter";

export const LoginFlow = ({ title, testPrefix, allowForgot }) => {
  const { login, verifyTotp } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme } = useTheme();
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfa, setMfa] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const run = async (fn) => {
    setError("");
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(apiErrorMessage(err, t("common.error")));
    } finally {
      setBusy(false);
    }
  };

  const submitCredentials = (e) => {
    e.preventDefault();
    run(async () => {
      const data = await login(email, password);
      setMfa(data);
      setStep(data.step);
    });
  };
  const submitForgot = (e) => {
    e.preventDefault();
    run(async () => { await axios.post(`${API}/auth/forgot-password`, { email }); setForgotSent(true); });
  };
  const submitCode = (e) => {
    e.preventDefault();
    run(() => verifyTotp(mfa.token, code, step === "totp_setup"));
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10 bg-background text-foreground">
      <img src={theme === "dark" ? "/logos/rapidpay-white.png" : "/logos/rapidpay-color.webp"} alt="RapidPay" className="h-10 mb-8" data-testid={`${testPrefix}-logo`} />
      <div className="w-full max-w-md rounded-2xl shadow-lg p-6 sm:p-8 bg-card border border-border">
        <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight mb-1">{title}</h1>
        <p className="text-sm mb-6 text-muted-foreground">
          {step === "credentials" ? t("login.subtitle") : step === "forgot" ? t("login.forgotHelp") : step === "totp_setup" ? t("login.totpSetupHelp") : mfa?.method === "email" ? t("login.emailCodeHelp", { hint: mfa.hint || "" }) : t("login.totpHelp")}
        </p>

        {step === "credentials" && (
          <form onSubmit={submitCredentials} className="space-y-4">
            <Field label={t("login.email")}>
              <Input testId={`${testPrefix}-email-input`} type="email" inputMode="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label={t("login.password")}>
              <Input testId={`${testPrefix}-password-input`} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            {error && <p data-testid={`${testPrefix}-error`} className="text-sm text-destructive">{error}</p>}
            <Btn type="submit" busy={busy} testId={`${testPrefix}-submit-button`} className="w-full h-12 text-base">{t("login.signIn")}</Btn>
            {allowForgot && <Link to="/registrarse" data-testid={`${testPrefix}-register-button`} className="inline-flex w-full items-center justify-center h-12 rounded-full border border-primary/60 text-[hsl(122,87%,28%)] dark:text-primary font-bold hover:bg-primary/10 transition-colors">{t("login.register")}</Link>}
            {allowForgot && <button type="button" data-testid={`${testPrefix}-forgot-link`} onClick={() => { setError(""); setStep("forgot"); }} className="block w-full text-center text-sm font-semibold text-muted-foreground hover:text-foreground underline-offset-4 hover:underline min-h-[44px]">{t("login.forgot")}</button>}
          </form>
        )}

        {step === "forgot" && (
          forgotSent ? (
            <div className="space-y-4" data-testid={`${testPrefix}-forgot-sent`}>
              <p className="text-sm">{t("login.forgotSent", { email })}</p>
              <Btn type="button" variant="outline" onClick={() => { setStep("credentials"); setForgotSent(false); }} testId={`${testPrefix}-forgot-back`} className="w-full h-12">{t("common.back")}</Btn>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4" data-testid={`${testPrefix}-forgot-form`}>
              <Field label={t("login.email")}>
                <Input testId={`${testPrefix}-forgot-email`} type="email" inputMode="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </Field>
              {error && <p data-testid={`${testPrefix}-forgot-error`} className="text-sm text-destructive">{error}</p>}
              <Btn type="submit" busy={busy} testId={`${testPrefix}-forgot-submit`} className="w-full h-12 text-base">{t("login.forgotSubmit")}</Btn>
              <button type="button" onClick={() => { setError(""); setStep("credentials"); }} data-testid={`${testPrefix}-forgot-cancel`} className="block w-full text-center text-sm font-semibold text-muted-foreground hover:text-foreground min-h-[44px]">{t("common.back")}</button>
            </form>
          )
        )}

        {(step === "totp_required" || step === "totp_setup") && (
          <form onSubmit={submitCode} className="space-y-4">
            {step === "totp_setup" && mfa?.qr && (
              <div className="flex flex-col items-center gap-3">
                <img src={mfa.qr} alt="QR TOTP" className="w-44 h-44 rounded-lg bg-white p-2" data-testid={`${testPrefix}-totp-qr`} />
                <p className="text-xs text-muted-foreground">{t("login.totpSecret")}</p>
                <code className="text-xs font-mono px-3 py-1.5 rounded-md bg-secondary" data-testid={`${testPrefix}-totp-secret`}>{mfa.secret}</code>
              </div>
            )}
            <Field label={t("login.code")}>
              <Input testId={`${testPrefix}-totp-input`} type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="text-center text-xl tracking-[0.5em] font-mono" />
            </Field>
            {error && <p data-testid={`${testPrefix}-totp-error`} className="text-sm text-destructive">{error}</p>}
            <Btn type="submit" busy={busy} disabled={code.length !== 6} testId={`${testPrefix}-totp-submit-button`} className="w-full h-12 text-base">{t("login.verify")}</Btn>
          </form>
        )}

        <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="w-3.5 h-3.5 text-[hsl(122,87%,30%)] dark:text-primary" /> {t("login.securedBy")}
          </span>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <button type="button" data-testid={`${testPrefix}-language-toggle`} onClick={() => setLang(lang === "es" ? "en" : "es")} className="text-xs font-semibold px-3 py-2 min-h-[44px] rounded-full text-foreground hover:bg-secondary transition-colors">
              {lang === "es" ? "EN" : "ES"}
            </button>
          </div>
        </div>
      </div>
      <StatusFooter className="w-full max-w-md mt-6 border-t-0 pt-0" />
    </div>
  );
};
